/** A filter value the report-builder agent could not match exactly. The agent has already dropped
 * it from the drafted definition, so a report confirmed from this draft would not apply it. */
export interface UnresolvedFlag {
	/** The value as the user wrote it. `''` when the flag arrived without one. */
	term: string;
	/** The agent's own explanation, falling back to `term`. `''` when the flag carried neither. */
	message: string;
	/** Real values the user can pick instead. Always `[]` when `term` is `''`, because a pick has to
	 * quote the unmatched term back to the agent. */
	candidates: string[];
}

const UNRESOLVED_FLAG_TYPE = 'unresolved';

/** True for any flag object of the unresolved type, however malformed the rest of it is. A malformed
 * unresolved flag must still block Confirm, so this check never looks past `type`. */
export function isUnresolvedFlag(flag: unknown): boolean {
	return !!flag && typeof flag === 'object' && (flag as {type?: unknown}).type === UNRESOLVED_FLAG_TYPE;
}

const trimmedString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export function unresolvedFlagsOf(flags: unknown): UnresolvedFlag[] {
	if (!Array.isArray(flags)) {
		return [];
	}
	return flags.filter(isUnresolvedFlag).map(flag => {
		const record = flag as {term?: unknown; message?: unknown; candidates?: unknown};
		const term = trimmedString(record.term);
		const message = trimmedString(record.message) || term;
		const candidates =
			term && Array.isArray(record.candidates)
				? Array.from(new Set(record.candidates.map(trimmedString).filter(candidate => candidate.length > 0)))
				: [];
		return {term, message, candidates};
	});
}

export function hasUnresolved(flags: unknown): boolean {
	return Array.isArray(flags) && flags.some(isUnresolvedFlag);
}
